import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'

import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import Avatar from '@material-ui/core/Avatar'
import IconButton from '@material-ui/core/IconButton'
import Popover from '@material-ui/core/Popover'
import Slider from '@material-ui/core/Slider'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import Switch from '@material-ui/core/Switch'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import TuneIcon from '@material-ui/icons/Tune'
import withWidth, { isWidthUp } from "@material-ui/core/withWidth";

import {
    usePaletteRange,
    useSavePaletteRange,
    useResetPaletteRange,
    useAutoFitPalette,
    usePaletteOptions,
} from '../../adapters/paletteRange'
import { legendNumericRange, paletteSliderBounds, rescaleLegend } from '../../utils/palette'
import GraniotSnackbar from '../../utils/snackbar'

const useStyles = makeStyles((theme) => ({
    imgLegend: {
        padding: 6,
        width: theme.spacing(5),
        position: 'absolute',
        bottom: 90,
        right: 12,
        zIndex: 1000,
        borderRadius: 12,
        backgroundColor: '#fff',
        border: '1.5px solid rgba(0,0,0,0.1)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    },
    swatchRamp: {
        borderRadius: 6,
        overflow: 'hidden',
    },
    small: {
        width: '100%',
        height: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            height: theme.spacing(3),
        },
    },
    tuneButton: {
        display: 'flex',
        margin: '4px auto 0',
        padding: 4,
    },
    popover: {
        padding: theme.spacing(2),
        width: 260,
    },
    sliderRow: {
        padding: theme.spacing(4, 1, 0),
    },
    shadeToggle: {
        marginLeft: 0,
        marginTop: theme.spacing(1),
    },
    actions: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(1),
    },
}))

function PaletteRangePopover({ layer, farm, date, shadingAvailable, anchorEl, onClose }) {
    const classes = useStyles()
    const { t } = useTranslation()

    const savedRange = usePaletteRange(farm?.id, layer.name)
    const saveRange = useSavePaletteRange()
    const resetRange = useResetPaletteRange()
    const autoFit = useAutoFitPalette()
    const [error, setError] = React.useState(null)

    const bounds = paletteSliderBounds(layer.legend)
    const defaultRange = legendNumericRange(layer.legend)
    const currentRange = savedRange || { min: defaultRange.lo, max: defaultRange.hi }
    const shade = !!savedRange?.shade
    const [value, setValue] = React.useState([currentRange.min, currentRange.max])

    React.useEffect(() => {
        setValue([currentRange.min, currentRange.max])
    }, [currentRange.min, currentRange.max])

    const busy = saveRange.isLoading || resetRange.isLoading || autoFit.isLoading

    const handleCommit = (_, [min, max]) => {
        if (min < max) saveRange.mutate({ farmId: farm.id, layerName: layer.name, min, max, shade })
    }

    const handleShadeChange = (event) => {
        saveRange.mutate({
            farmId: farm.id,
            layerName: layer.name,
            min: currentRange.min,
            max: currentRange.max,
            shade: event.target.checked,
        })
    }

    const handleAutoFit = () => {
        autoFit.mutate(
            { farmId: farm.id, layerKey: layer.key, date },
            {
                onSuccess: ({ min, max }) =>
                    saveRange.mutate({ farmId: farm.id, layerName: layer.name, min, max, shade }),
                onError: (err) =>
                    setError(
                        err?.error_code === 'NO_STATS_FOR_DATE'
                            ? t('Palette_range.no_stats_for_date')
                            : t('Palette_range.error')
                    ),
            }
        )
    }

    return (
        <>
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
            transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        >
            <div className={classes.popover}>
                <Typography variant="subtitle2">{t('Palette_range.title')}</Typography>
                <Typography variant="caption" color="textSecondary">
                    {t('Palette_range.description')}
                </Typography>
                <div className={classes.sliderRow}>
                    <Slider
                        value={value}
                        min={bounds.min}
                        max={bounds.max}
                        step={0.01}
                        valueLabelDisplay="on"
                        disabled={busy}
                        onChange={(_, newValue) => setValue(newValue)}
                        onChangeCommitted={handleCommit}
                    />
                </div>
                {shadingAvailable && (
                    <Tooltip title={t('Palette_range.vegetation_texture_help')} placement="left">
                        <FormControlLabel
                            className={classes.shadeToggle}
                            control={
                                <Switch
                                    size="small"
                                    color="primary"
                                    checked={shade}
                                    disabled={busy}
                                    onChange={handleShadeChange}
                                />
                            }
                            label={<Typography variant="body2">{t('Palette_range.vegetation_texture')}</Typography>}
                        />
                    </Tooltip>
                )}
                <div className={classes.actions}>
                    <Tooltip title={date ? '' : t('Palette_range.select_date')}>
                        <span>
                            <Button
                                size="small"
                                color="primary"
                                variant="outlined"
                                disabled={busy || !date}
                                onClick={handleAutoFit}
                                startIcon={autoFit.isLoading ? <CircularProgress size={14} /> : null}
                            >
                                {t('Palette_range.auto')}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        size="small"
                        disabled={busy || !savedRange}
                        onClick={() => resetRange.mutate({ farmId: farm.id, layerName: layer.name })}
                    >
                        {t('Palette_range.reset')}
                    </Button>
                </div>
            </div>
        </Popover>
        <GraniotSnackbar
            open={!!error}
            setOpen={() => setError(null)}
            status="error"
            message={error}
        />
        </>
    )
}

function Legend({ layersState, layer, width, adjustable = false, farm, date }) {
    const classes = useStyles()
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = React.useState(null)
    const hasNumericLegend = !!legendNumericRange(layer.legend)
    const { data: paletteOptions } = usePaletteOptions(layer.key, {
        enabled: adjustable && hasNumericLegend,
    })
    // Ranges are saved per farm: the legend adjusts the selected (first checked) farm
    const canAdjust = adjustable && !!farm && hasNumericLegend && !!paletteOptions?.adjustable
    const savedRange = usePaletteRange(farm?.id, canAdjust ? layer.name : null)

    if (!layer.legend || layer.legend.length === 0) return null;

    const legend = canAdjust ? rescaleLegend(layer.legend, savedRange) : layer.legend

    return (
        <Paper className={classes.imgLegend} elevation={0}>
            <Grid container className={classes.swatchRamp}>
                {legend
                    ?.slice(0)
                    .reverse()
                    .map((l, index) => (
                        <Grid key={index} item xs={12}>
                            <Avatar
                                variant="square"
                                className={classes.small}
                                style={{
                                    backgroundColor: l.color,
                                    fontSize: (isWidthUp("sm", width) ? 12 : 10),
                                    height:
                                        (isWidthUp("sm", width) ? 210 : 200) /
                                        legend.length,
                                }}
                            >
                                {l.label}
                            </Avatar>
                        </Grid>
                    ))}
            </Grid>
            {canAdjust && (
                <>
                    <Tooltip title={t('Palette_range.title')} placement="left">
                        <IconButton
                            size="small"
                            className={classes.tuneButton}
                            color={savedRange ? 'primary' : 'default'}
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                        >
                            <TuneIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {anchorEl && (
                        <PaletteRangePopover
                            layer={layer}
                            farm={farm}
                            date={date}
                            shadingAvailable={!!paletteOptions?.shading}
                            anchorEl={anchorEl}
                            onClose={() => setAnchorEl(null)}
                        />
                    )}
                </>
            )}
        </Paper>
    )
}

export default withWidth()(Legend);
